import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { IMAGES } from "../lib/images";
import { fileUrl } from "../lib/storage";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <Layout>
        <section className="min-h-[60vh] pt-40 px-6 lg:px-12 flex flex-col items-center justify-center text-center">
          <div className="overline mb-3">— Not Found</div>
          <h1 className="font-serif text-4xl">This development is not currently visible.</h1>
          <Link to="/projects" className="mt-6 text-primary underline">← Back to developments</Link>
        </section>
      </Layout>
    );
  }
  if (!project) {
    return <Layout><div className="min-h-[60vh] pt-40 flex items-center justify-center"><div className="overline">Loading</div></div></Layout>;
  }

  const heroImg = project.hero_image_url
    ? fileUrl(project.hero_image_url)
    : (IMAGES[project.hero_image] || IMAGES.elliott_twin_towers);

  return (
    <Layout>
      <SEO title={project.name} description={project.tagline || project.description} path={`/projects/${project.id}`} image={heroImg} />
      <section className="relative h-[80vh] min-h-[600px] overflow-hidden">
        <img src={heroImg} alt={project.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/30 to-background" />
        <div className="relative h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16">
          <div className="overline mb-4">— {project.status} · {project.type}</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.0] font-light tracking-tight">
            {project.name}
          </h1>
          <p className="mt-6 text-lg text-foreground/80 max-w-2xl">{project.tagline}</p>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline">— Overview</div>
            <dl className="mt-6 space-y-5 font-mono text-sm">
              <div><dt className="overline text-foreground/50">Location</dt><dd className="text-primary mt-1">{project.location}</dd></div>
              <div><dt className="overline text-foreground/50">Type</dt><dd className="text-foreground mt-1">{project.type}</dd></div>
              <div><dt className="overline text-foreground/50">Timeline</dt><dd className="text-foreground mt-1">{project.year}</dd></div>
              <div><dt className="overline text-foreground/50">Status</dt><dd className="text-foreground mt-1">{project.status}</dd></div>
            </dl>
          </div>
          <div className="lg:col-span-8">
            <p className="font-serif text-2xl md:text-3xl leading-[1.25] font-light text-balance">{project.description}</p>
            {project.features && (
              <ul className="mt-10 grid sm:grid-cols-2 gap-4">
                {project.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 border-l border-primary/30 pl-4">
                    <span className="text-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/contact" data-testid="project-detail-cta" className="mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors">
              Inquire <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
